/* eslint-disable jsx-a11y/click-events-have-key-events */
import React from "react";
import checkIcon from "images/check-bold.svg";
import priceTag from "images/tag.svg";
import TestimonialCard1 from "images/boost/boost_testimonial_card-1.webp";
import TestimonialCard2 from "images/boost/boost_testimonial_card-2.webp";
import TestimonialCard3 from "images/boost/boost_testimonial_card-3.webp";
import TestimonialCard4 from "images/boost/boost_testimonial_card-4.webp";
import { boostProductTierConfig } from "../consts";
import { calculatePriceAfterPromotion } from "../utils";

/* --- UTIL FUNCTIONS --- */

/**
 *
 * @param {number} current - The current price
 * @param {number} original - The original price
 * @returns {number} discount percentage
 */
export const calculateDiscount = (current, original) => {
  return Math.round((1 - current / original) * 100);
};

/**
 *
 * @param {string} selectedTerm - The selected term
 * @param {number} price - The current price
 * @param {number} originalPrice - The original price
 * @returns {number} perMonthPrice - The price per month
 */
export const calculatePerMonthPrice = (selectedTerm, price, originalPrice) => {
  const perMonthPrice = selectedTerm === "yearly" ? price / 12 : price;
  const perMonthOriginalPrice =
    selectedTerm === "yearly" ? originalPrice / 12 : originalPrice;
  return { perMonthPrice, perMonthOriginalPrice };
};

/* --- COMPONENTS --- */

export const ProductTermToggle = ({
  checkoutTerm,
  setCheckoutTerm,
  belowMd,
}) => {
  return (
    <div
      className="flex border-md p-1 rounded-full mx-auto bg-white text-black bg-opacity-50"
      style={{ width: belowMd ? 250 : 350 }}
    >
      <button
        type="button"
        style={{ transition: "all 0.3s ease" }}
        onClick={() => setCheckoutTerm("monthly")}
        className={`relative font-semibold text-xs md:text-md rounded-full p-1 md:p-3 transition-colors focus:outline-none w-1/2 ${
          checkoutTerm === "monthly" ? "bg-white" : ""
        }`}
      >
        monthly plan
      </button>
      <button
        type="button"
        onClick={() => setCheckoutTerm("yearly")}
        style={{ transition: "all 0.3s ease" }}
        className={`relative font-semibold text-xs md:text-md rounded-full p-1 md:p-3 transition-colors focus:outline-none w-1/2 ${
          checkoutTerm === "yearly" ? "bg-white" : ""
        }`}
      >
        1-year plan
      </button>
    </div>
  );
};

export const PromoAppliedTag = ({ className="text-sm" }) => {
  return (
    <span className={`flex justify-center items-center ${className}`}>
      <img className="mr-2" alt="" src={priceTag}/>
      Promo code applied
    </span>
  );
};

/**
 *
 * @param {Object} props
 * @param {string} props.label - The label of the tier
 * @returns
 */
const TierLabel = ({ label }) => {
  return (
    <span
      style={{
        color: "#FFFFFF",
        backgroundColor: "#000000",
        width: 124,
        top: -15,
      }}
      className="absolute left-0 right-0 m-auto top-1/2 flex justify-center items-center gap-2.5 py-1 px-1.5 text-lg font-bold"
    >
      {label}
    </span>
  );
};

/**
 *
 * @param {Object} props
 * @param {string} props.tierName - The name of the tier
 * @param {string} props.label - The name of the tier
 * @param {number} props.originalPrice - The original price
 * @param {number} props.price - The discounted price
 * @param {boolean} props.isMobile - The variant of the component
 * @returns
 */
export const TierPricingInfo = (props) => {
  const {
    tierName,
    label,
    originalPrice,
    price,
    isMobile,
    promotionApplied = false,
    isYearly = false,
    priceHidden = false,
  } = props;

  const discountPercentage = calculateDiscount(price, originalPrice);

  return (
    <div
      className={`flex flex-col h-full w-full ${isMobile ? "pt-6" : "pt-8"} px-6`}
    >
      {Boolean(label) && <TierLabel label={label} />}
      <div className="flex items-center justify-between">
        <p
          className={`text-white font-bold ${isMobile ? "text-2xl" : "text-3xl"} font-normal`}
        >
          {tierName}
        </p>
        <span
          style={{
            backgroundColor: "#87DDA4",
            paddingRight: 10,
            paddingLeft: 10,
          }}
          className="text-black text-center font-gilroy font-bold text-sm mr-2 rounded-xl py-0.5"
        >
          {discountPercentage}% off
        </span>
      </div>
      {!priceHidden && <div
        className="flex flex-col"
        style={{ marginTop: isMobile ? 14 : 8, lineHeight: "normal" }}
      >
        <span className="text-white font-bold">
          <span style={{ fontSize: 40 }}>${price.toFixed(2)}</span>
          <span className="text-2xl">/{isYearly ? "yr" : "mo"}</span>
        </span>
        {promotionApplied && (
          <span className="mt-1 mb-2 self-start">
            <PromoAppliedTag />
          </span>
        )}
        <span
          style={{ color: "#9C9C9C" }}
          className="text-base font-normal line-through font-strikethrough"
        >
          ${originalPrice.toFixed(2)}/{isYearly ? "yr" : "mo"}
        </span>
      </div>}
    </div>
  );
};

/**
 *
 * @param {Object} props
 * @param {string} props.title - The button title
 * @param {function} props.onClick - onClick handler
 * @param {boolean} props.isHighlighted - Whether the tier is highlighted
 * @returns
 */
export const JoinButton = (props) => {
  const { title, onClick, isHighlighted } = props;

  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-10 rounded items-center justify-center self-center px-4 text-lg font-medium hover:text-white ${isHighlighted ? "bg-boost-primary-500 hover:bg-boost-primary-400 text-white" : "bg-white hover:bg-boost-primary-500 text-black"} `}
    >
      <span>{title}</span>
    </button>
  );
};

/**
 *
 * @param {Object} props
 * @param {Object} props.feature - The feature object
 * @param {string} props.feature.title - The title of the feature
 * @param {Array.<{text: string, style: React.CSSProperties}>} props.feature.description - The perks of the feature
 * @returns
 */
export const TierFeature = ({ feature }) => {
  return (
    <span
      key={feature.title}
      className="flex items-center"
      style={{ lineHeight: "normal" }}
    >
      <img
        src={checkIcon}
        alt="check"
        style={{
          width: 16,
          height: 16,
        }}
      />
      {feature.description.map(({ text, style }) => (
        <span className="text-base ml-1" style={style}>
          {text}
        </span>
      ))}
    </span>
  );
};

/**
 *
 * @param {Object} props
 * @param {string} props.tier - The tier name
 * @param {string} props.selectedTerm - The selected term
 * @param {function} props.handleClick - onClick handler
 * @param {boolean} props.isHighlighted - Whether the tier is highlighted
 * @returns
 */
export const TierTileMobile = ({
  tier,
  selectedTerm,
  handleClick,
  isHighlighted,
}) => {
  const tierConfig = boostProductTierConfig[selectedTerm]?.[tier];
  const {
    name,
    price = 0,
    originalPrice = 0,
    label,
    featuresMobile,
  } = tierConfig || {};

  const { perMonthPrice, perMonthOriginalPrice } = calculatePerMonthPrice(
    selectedTerm,
    price,
    originalPrice,
  );

  return (
    <div
      className={`relative ${isHighlighted ? "p-1" : "p-px"} flex`}
      style={{
        borderRadius: 10,
        minWidth: 257,
        minHeight: 554,
        background: `linear-gradient(to bottom, ${
          isHighlighted ? "#BB99FF, #BB99FF1A" : "#B7B7B7, #BB99FF1A"
        })`,
      }}
    >
      <div
        className={`flex-1 flex flex-col bg-black ${
          isHighlighted ? "p-1" : "p-px"
        } pb-6`}
        style={{ borderRadius: 10 }}
      >
        <div>
          <TierPricingInfo
            tierName={name}
            price={perMonthPrice}
            originalPrice={perMonthOriginalPrice}
            label={label}
            isMobile
          />
        </div>
        <div
          style={{
            marginTop: 24,
            marginLeft: 28,
            width: 50,
            height: 1,
            backgroundColor: "#5B5B5B",
          }}
        />
        <div className="flex flex-1 flex-col mt-2 pl-6 pr-3">
          {featuresMobile.map((feature) => (
            <div key={feature.title} className="mt-4">
              <TierFeature feature={feature} />
            </div>
          ))}
        </div>
        <div className="mt-6 self-center">
          <JoinButton
            title={`Get ${name}`}
            onClick={handleClick}
            isHighlighted={isHighlighted}
          />
        </div>
      </div>
    </div>
  );
};

export const TierTileCheckoutSnapshot = ({
  tier,
  selectedTerm,
  isHighlighted,
  promotionApplied,
  price,
}) => {
  const tierConfig = boostProductTierConfig[selectedTerm]?.[tier];
  const isYearly = selectedTerm === "yearly";
  const {
    name,
    originalPrice = 0,
    label,
    featuresMobile,
  } = tierConfig || {};

  return (
    <div>
      <div
        className={`relative ${isHighlighted ? "p-1" : "p-px"} flex`}
        style={{
          borderRadius: 10,
          width: 312,
          minHeight: 554,
          background: `linear-gradient(to bottom, ${
            isHighlighted ? "#BB99FF, #040404" : "#B7B7B7, #BB99FF1A"
          })`,
        }}
      >
        <div
          className={`flex-1 flex flex-col bg-black p-${
            isHighlighted ? "1" : "px"
          } pb-6`}
          style={{ borderRadius: 10 }}
        >
          <div>
            <TierPricingInfo
              tierName={name}
              price={price}
              originalPrice={originalPrice}
              label={label}
              promotionApplied={promotionApplied}
              isYearly={isYearly}
              priceHidden
              isMobile
            />
          </div>
          <div
            style={{
              marginTop: 24,
              marginLeft: 28,
              width: 50,
              height: 1,
              backgroundColor: "#5B5B5B",
            }}
          />
          <div className="flex flex-1 flex-col mt-2 pl-6 pr-3">
            {featuresMobile.map((feature) => (
              <div key={feature.title} className="mt-4">
                <TierFeature feature={feature} />
              </div>
            ))}
          </div>
        </div>
      </div>
      <div
        className="relative"
        style={{ width: 325 }}
      >
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-r from-transparent via-transparent to-black opacity-75 pointer-events-none z-10" />
        <TestimonialCards cardWidth={270} />
      </div>
    </div>
  );
};

export const OrderSummary = ({ productTerm, productTier, promotionCredit, promotionPercentOff }) => {
  const selectedConfig =
  boostProductTierConfig[productTerm]?.[productTier] || {};
  const { price } = selectedConfig;
  const promotionApplied = promotionCredit > 0 || promotionPercentOff > 0;
  const priceAfterDiscount = calculatePriceAfterPromotion(
    price,
    promotionPercentOff,
    promotionCredit,
  );
  const promotionAmount = (price - priceAfterDiscount).toFixed(2);

  return (
    <div className="text-black flex flex-col space-y-2 mb-5">
      <div className="font-semibold">Order Summary</div>
      <div className="flex justify-between">
        <span>{`${productTier} (${productTerm})`}</span>
        <span className={`${promotionApplied ? "line-through" : ""}`}>${price}</span>
      </div>
      {promotionApplied && <div className="flex justify-between" style={{ color: "#107332" }}>
        <span className="flex"><PromoAppliedTag className="text-md mr-2" />{`(${promotionPercentOff}% off)`}</span>
        <span>-${promotionAmount}</span>
      </div>}
      <div className="border-b-2 border-neutral-400" />
      <div className="flex justify-between font-semibold">
        <span>Total</span>
        <span>${priceAfterDiscount}</span>
      </div>
    </div>
  );
};

export const TestimonialCards = ({ cardWidth = 295 }) => {
  return (
    <div className="-ml-4 flex overflow-x-scroll hide-x-scrollbar flex-shrink-0">
      <div className="px-4 py-4 flex self-start overflow-visible">
        {
          [TestimonialCard1, TestimonialCard2, TestimonialCard3, TestimonialCard4]
            .map((imageSrc, idx) => (
              <img
                alt=""
                src={imageSrc}
                // eslint-disable-next-line react/no-array-index-key
                key={`testimonial-${idx}`}
                style={{ width: cardWidth, height: "auto" }}
                className="pr-6 box-content transform hover:scale-110 transition-transform"
              />
            ))
        }
      </div>
    </div>
  );
};